import { Add as AddIcon } from "@mui/icons-material";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { fmtBigInteger } from "@utils/format";
import { ProviderFile } from "fileParsing/types";
import React from "react";
import { WILDCARD, Path, SupposedUsage, TypeSchema, UnionType, Visibility } from "fileParsing/wrangler/types";
import { DragChip } from "./Chip";
import { extractRandomSample } from "fileParsing/wrangler/extractTemplates";

type ComponentProps = {
  tree: UnionType | TypeSchema;
  files: ProviderFile[];
  selected: Path[];
  hovered?: string;
  setHovered: (path: Path | undefined) => void;
  visibilities: [Path, Visibility][];
  onChange: (template: [Path, Visibility]) => void;
};

type RecProps = {
  tree: UnionType | TypeSchema;
  path: Path;
  optional: boolean;
  keyPrefix: string;
};

type KVPairProps = {
  k: string;
  v: UnionType;
  path: Path;
  optional?: boolean;
  keyPrefix?: string;
};

const couldContainDataPoints = (tree: UnionType): boolean => {
  return tree.some(
    (i) =>
      i.type === "array" &&
      i.anyOf.some(
        (j) => j.type === "string" || j.type === "object" || (j.type === "array" && couldContainDataPoints([j])),
      ),
  );
};

/**
 * Formats and returns the count of elements in the specified path of the data structure
 * contained in an array of provider files.
 *
 * @param {Path} path - The path of the data structure for which the count of elements is to be calculated and formatted.
 * @param {ProviderFile[]} files - The array of provider files containing the data structure for which the count of elements is to be calculated and formatted.
 * @returns {string} The formatted count of elements in the specified path of the data structure.
 */
function formatElementCount(path: Path, files: ProviderFile[]): string {
  const createElementCount = (tree: unknown, path: Path): number[] => {
    if (path.length === 0) {
      return [Array.isArray(tree) ? tree.length : 0];
    } else if (path[0] === WILDCARD) {
      // TODO: sometimes tree is an object here
      return (Array.isArray(tree) ? (tree as unknown[]) : [])
        .map((t: unknown) => createElementCount(t, path.slice(1)))
        .reduce<number[]>((result, arr) => [...result, ...arr], []);
    } else if (typeof path[0] === "number") {
      if (tree && Array.isArray(tree)) return createElementCount((tree as unknown[])[path[0]], path.slice(1));
    } else {
      const rec = tree as Record<string, unknown>;
      if (path[0] in rec && rec?.[path[0]]) {
        const { [path[0]]: pVal } = rec;
        return [...createElementCount(pVal, path.slice(1))];
      }
    }
    return [];
  };

  const dataPath = "data";
  if (path.toString() === dataPath) {
    return fmtBigInteger(files.length);
  }
  const elCount = files.reduce(
    (maxmin: number[], f) => maxmin.concat(createElementCount(f.parserData?.structure, [...path])),
    [0, 0],
  );
  if (elCount.length === 0) return "";
  if (elCount.length === 1) return fmtBigInteger(elCount[0]);
  const min = fmtBigInteger(Math.min(...elCount));
  const max = fmtBigInteger(Math.max(...elCount));
  if (min === max) return min;
  return `${min} - ${max}`;
}

type VisibilityProps = {
  visibility?: Visibility;
  onChange: (vis: Visibility) => void;
};

const VisibilityCtrl: React.FC<VisibilityProps> = ({ visibility, onChange }) => (
  <>
    {visibility === Visibility.hidden ? (
      <Button size="small" onClick={() => onChange(Visibility.visible)}>
        <AddIcon fontSize="small" />
        Add
      </Button>
    ) : null}
  </>
);

const FileTree: React.FC<ComponentProps> = ({ tree, files, selected, hovered, setHovered, visibilities, onChange }) => {
  const sampleValue = (path: Path): string => {
    const offset = Math.floor(Math.random() * files.length);
    for (let i = 0; i < files.length; i++) {
      const ix = (i + offset) % files.length;
      const sample = extractRandomSample(files[ix].parserData.structure, path);
      if (sample !== undefined) return String(sample);
    }
    return "";
  };

  const KVPair: React.FC<KVPairProps> = ({ k, v, path, optional = false, keyPrefix }) => {
    const opt = v.some((t) => t.type === "empty");
    const key = `${k}${opt ? " (optional)" : ""}`;

    if (couldContainDataPoints(v)) {
      return (
        <Box
          sx={{
            borderLeft: "1px solid #333",
          }}
        >
          {/* HEADER */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              margin: 0.25,
              padding: 0,
              background: "linear-gradient(135deg, #333f, #3330)",
            }}
          >
            <Typography sx={{ color: "#baffba", ml: 1 }}>[{formatElementCount(path, files)}]</Typography>
            <Typography sx={{ display: "block" }}>
              {keyPrefix}
              {keyPrefix ? "." : ""}
              {key}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <VisibilityCtrl
              visibility={visibilities.find(([p]) => p.toString() === path.toString())?.[1] ?? undefined}
              onChange={(v) => onChange([path, v])}
            />
          </Stack>
          {/* CONTENT */}
          <Box
            sx={{
              marginLeft: 4,
              paddingLeft: 4,
            }}
          >
            <RecursiveTree tree={v} path={path} optional={opt || optional} keyPrefix={""} />
          </Box>
        </Box>
      );
    } else {
      // Can't contain any further datapoints

      // Arrays and objects are not selectable
      const selectable = !v.some((t) => t.type === "object" || t.type === "array");
      if (selectable) {
        const usages = v.reduce(
          (usages: SupposedUsage[], t) =>
            "supposedUsage" in t && t.supposedUsage && usages.every((u) => t.supposedUsage !== u)
              ? [...usages, t.supposedUsage]
              : usages,
          [],
        );
        const isSelected = selected.some((s) => path.toString() === s.toString());
        return (
          <Tooltip
            sx={{ p: 1 }}
            title={
              <Typography>
                <b>Sample value:</b> {sampleValue(path)}
              </Typography>
            }
            followCursor
          >
            <span>
              <DragChip
                hovered={hovered === path.toString()}
                label={(keyPrefix ?? "") + (keyPrefix ? "." : "") + key}
                onHover={(b) => setHovered(b ? path : undefined)}
                path={path}
                selected={isSelected}
                usage={usages.length === 1 ? usages[0] : undefined}
              />
            </span>
          </Tooltip>
        );
      }

      // Sill does not contain datapoints, may be/contain
      // objects with draggable fields
      return (
        <RecursiveTree
          tree={v}
          path={path}
          optional={opt || optional}
          keyPrefix={(keyPrefix ?? "") + (keyPrefix ? "." : "") + key}
        />
      );
    }
  };

  const RecursiveTree: React.FC<RecProps> = ({ path, optional, keyPrefix, tree }) => {
    if (tree !== undefined) {
      if (Array.isArray(tree)) {
        // tree is UnionType
        const nonEmptyTypes = tree.filter((t) => t.type !== "empty");
        return (
          <>
            {nonEmptyTypes.map((t) => (
              <RecursiveTree key={t.type} tree={t} path={path} optional={optional} keyPrefix={keyPrefix} />
            ))}
          </>
        );
      } else if ("type" in tree) {
        if (tree.type === "array") {
          const nested = tree.anyOf.filter((t) => t.type === "string" || t.type === "array" || t.type === "object");
          if (nested.length >= 1) {
            // Array with Content
            return (
              <>
                {nested.map((t) => (
                  <RecursiveTree
                    key={`${path}-${t.type}`}
                    tree={t}
                    path={[...path, null]}
                    optional={optional}
                    keyPrefix={keyPrefix}
                  />
                ))}
              </>
            );
          } else {
            // Empty Array
            const typeList = tree.anyOf
              .filter((t) => t.type !== "empty")
              .map((t) => t.type)
              .join(", ");
            return (
              <Typography variant="caption">{typeList.length > 0 ? `\u2026 (${typeList})` : " - empty - "}</Typography>
            );
          }
        } else if (tree.type === "object") {
          // Object with Key Value Pairs
          return (
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={0.5}>
              {Object.entries(tree.properties).map(([k, v]: [string, UnionType]) => (
                <KVPair key={k} k={k} v={v} path={[...path, k]} keyPrefix={keyPrefix} />
              ))}
            </Stack>
          );
        } else {
          console.log("Tree is empty but not undefined??");
          console.log(tree);
        }
      }
    }
    return null;
  };

  return <RecursiveTree tree={tree} path={[]} optional={false} keyPrefix="" />;
};

export default FileTree;
